import { CollectionReference, collection, doc, getDoc, getFirestore } from 'firebase/firestore';

import { toast } from '@/components/Toaster/Toast';

import {
  AccSensitivity,
  CalibrationMovementLimit,
  GyroSensitivity,
  HubDataMode,
  SamplingRate,
  SensorDataMode,
} from '@/models';

import { safePromise } from './safePromise';

export interface SystemConfig {
  accSensitivity: AccSensitivity;
  calibrationMovementLimit: CalibrationMovementLimit;
  dataBuffering: string;
  dataMode: SensorDataMode;
  gyrSensitivity: GyroSensitivity;
  hubDataMode: HubDataMode;
  name: string;
  samplingRate: SamplingRate;
  deviceState: string; // Hex string representing the device configuration
}

interface DeviceConfig {
  enable_offset_compensation?: boolean;
  enable_mag_field_mapping?: boolean;
  acc_sensitivity: number;
  gyr_sensitivity: number;
  sampling_rate: number;
  data_buffering: number;
  conn_interval: number;
}

export async function getFirebaseSystemConfigByConfigId(configId: string) {
  const firestore = getFirestore();

  const configRef = collection(firestore, '_config') as CollectionReference<SystemConfig>;

  // Get the config from the database
  const [data, error] = await safePromise(getDoc(doc(configRef, configId)));

  if (error) {
    toast({
      variant: 'error',
      title: 'Something went wrong',
      description: 'Please try again later',
    });

    return undefined;
  }

  // Check if the config exists
  if (!data || !data.exists()) {
    toast({
      variant: 'error',
      title: 'Could not find config',
      description: 'Please try again later',
    });

    return undefined;
  }

  // Get the config data
  const config = data.data();

  if (!config) {
    toast({
      variant: 'error',
      title: 'Config has no data',
      description: 'Please contact support',
    });

    return undefined;
  }

  if (config.name === 'ORYX GO') {
    try {
      const hexValue = systemConfigToHex(config);
      return {
        ...config,
        deviceState: hexValue, // Add the hex value to the config object
      };
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Config conversion failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
      return undefined;
    }
  }

  return config;
}

/**
 * Parse accelerometer sensitivity from Firebase format to numeric value
 */
function parseAccSensitivity(value: AccSensitivity): number {
  const map: Record<AccSensitivity, number> = {
    G2: 0,
    G16: 1,
    G4: 2,
    G8: 3,
    Undefined: 3, // default to 8g
  };
  return map[value] ?? 3;
}

/**
 * Parse gyroscope sensitivity from Firebase format to numeric value
 */
function parseGyrSensitivity(value: GyroSensitivity): number {
  const map: Record<GyroSensitivity, number> = {
    DPS250: 0,
    DPS125: 1,
    DPS500: 2,
    DPS1000: 4,
    DPS2000: 6,
    Undefined: 1, // default to 125dps
  };
  return map[value] ?? 1;
}

/**
 * Parse sampling rate from Firebase format to numeric value
 */
function parseSamplingRate(value: SamplingRate): number {
  const map: Record<SamplingRate, number> = {
    Hz1: 0,
    Hz2: 1,
    Hz4: 2,
    Hz5: 3,
    Hz10: 4,
    Hz20: 5,
    Hz25: 6,
    Hz50: 7,
    Hz100: 8,
    Undefined: 8, // default to Hz100
  };
  return map[value] ?? 8;
}

/**
 * Converts SystemConfig to device-specific numeric config
 */
function systemConfigToDeviceConfig(config: SystemConfig): DeviceConfig {
  return {
    acc_sensitivity: parseAccSensitivity(config.accSensitivity),
    gyr_sensitivity: parseGyrSensitivity(config.gyrSensitivity),
    // sampling_rate: parseSamplingRate(config.samplingRate)
    sampling_rate: 8, // Default value from 2M for the Hub
    data_buffering: 15, // Default value from 2M for the Hub
    conn_interval: 0, // Default value from 2M for the Hub
    enable_offset_compensation: false, // Default value
    enable_mag_field_mapping: false, // Default value
  };
}

/**
 * Converts device configuration parameters to a 32-bit hex value
 */
export function deviceConfigToHex(config: DeviceConfig): string {
  // Set defaults
  const defaults: DeviceConfig = {
    enable_offset_compensation: false,
    enable_mag_field_mapping: false,
    acc_sensitivity: 3,
    gyr_sensitivity: 1,
    sampling_rate: 8,
    data_buffering: 15,
    conn_interval: 0,
  };

  // Merge with defaults
  const cfg = { ...defaults, ...config };

  // Validate inputs
  if (cfg.acc_sensitivity < 0 || cfg.acc_sensitivity > 3) {
    throw new Error('acc_sensitivity must be 0-3');
  }
  if (![0, 1, 2, 4, 6].includes(cfg.gyr_sensitivity)) {
    throw new Error('gyr_sensitivity must be 0, 1, 2, 4, or 6');
  }
  if (cfg.sampling_rate < 0 || cfg.sampling_rate > 15) {
    throw new Error('sampling_rate must be 0-15');
  }
  if (cfg.data_buffering < 0 || cfg.data_buffering > 15) {
    throw new Error('data_buffering must be 0-15');
  }
  if (cfg.conn_interval < 0 || cfg.conn_interval > 255) {
    throw new Error('conn_interval must be 0-255');
  }

  // Build the 32-bit value
  let value = 0;

  // bit 0: enable_offset_compensation
  value |= (cfg.enable_offset_compensation ? 1 : 0) << 0;

  // bit 2: enable_mag_field_mapping
  value |= (cfg.enable_mag_field_mapping ? 1 : 0) << 2;

  // bit 11-12: acc_sensitivity (2 bits)
  value |= (cfg.acc_sensitivity & 0x3) << 11;

  // bit 13-15: gyr_sensitivity (3 bits)
  value |= (cfg.gyr_sensitivity & 0x7) << 13;

  // bit 16-19: sampling_rate (4 bits)
  value |= (cfg.sampling_rate & 0xf) << 16;

  // bit 20-23: data_buffering (4 bits)
  value |= (cfg.data_buffering & 0xf) << 20;

  // bit 24-31: com_interval (8 bits)
  value |= (cfg.conn_interval & 0xff) << 24;

  // Convert to hex string with 0x prefix and uppercase
  return '0x' + value.toString(16).toUpperCase().padStart(8, '0');
}

/**
 * Converts SystemConfig to hex value for ORYX GO devices
 */
export function systemConfigToHex(config: SystemConfig): string {
  if (config.name === 'ORYX GO') {
    const deviceConfig = systemConfigToDeviceConfig(config);
    return deviceConfigToHex(deviceConfig);
  }

  // For other devices, return a placeholder or throw an error
  throw new Error(`Config conversion not implemented for device: ${config.name}`);
}
